import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  AlertTitle,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  ListItemIcon,
  Popover,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';

import {
  getWorkspace,
  addRecording as addRecordingApi,
  removeRecording as removeRecordingApi,
  updateLayout as updateLayoutApi,
  updateNotes as updateNotesApi,
  updateTimelineMarkers as updateMarkersApi,
  updateRecordingMetadata as updateMetadataApi,
} from '../services/workspaceService';
import { getDataCapture, getDataCapturesList } from '../services/dataCaptureService';
import { getAllRules, createRule } from '../services/ruleService';
import LoadingSpinner from '../components/LoadingSpinner';
import Iconify from '../components/iconify';
import {
  UnifiedTimeline,
  WorkspaceGrid,
  WidgetPicker,
  TimelineProvider,
  computeTimelineBounds,
  computeAllEvents,
} from '../sections/@dashboard/workspace';
import { mergeActiveRules } from '../sections/@dashboard/workspace/ruleEngine';
import { needsMigration, migrateWorkspace } from '../sections/@dashboard/workspace/workspaceMigration';
import {
  getActiveTab,
  setActiveTab,
  addTab,
  removeTab,
  renameTab,
  reorderTabs,
  setDefaultTab,
  updateTab,
  removeRecordingWidgets,
} from '../sections/@dashboard/workspace/workspaceTabUtils';
import TabBar from '../sections/@dashboard/workspace/TabBar';
import WorkspaceSettingsDrawer from '../sections/@dashboard/workspace/WorkspaceSettingsDrawer';

const RECORDING_COLORS = [
  { value: '#1976d2', label: 'Blue' },
  { value: '#2e7d32', label: 'Green' },
  { value: '#d32f2f', label: 'Red' },
  { value: '#ed6c02', label: 'Orange' },
  { value: '#9c27b0', label: 'Purple' },
  { value: '#0288d1', label: 'Cyan' },
  { value: '#616161', label: 'Grey' },
];

export default function WorkspacePage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useOutletContext() || {};

  const [workspace, setWorkspace] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addRecordingDialogOpen, setAddRecordingDialogOpen] = useState(false);
  const [allRecordings, setAllRecordings] = useState([]);
  const [selectedRecordingIds, setSelectedRecordingIds] = useState([]);
  const [recordingSearch, setRecordingSearch] = useState('');
  const [removeRecordingId, setRemoveRecordingId] = useState(null);

  // Global state
  const [layoutConfig, setLayoutConfig] = useState({ tabs: [], activeTabId: null, defaultTabId: null, globalRules: [], globalActiveRuleIds: [], widgets: [] });
  const [globalNotes, setGlobalNotes] = useState('');
  const [globalMarkers, setGlobalMarkers] = useState([]);
  const [recordingMetadata, setRecordingMetadata] = useState({});
  const [apiRules, setApiRules] = useState([]); // rules from the API

  // Workspace settings
  const [workspaceSettings, setWorkspaceSettings] = useState({ skipEmptyTime: false });
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);

  // Feature preview warning (show once per device)
  const [showPreviewWarning, setShowPreviewWarning] = useState(() => !localStorage.getItem('vpsi_workspace_preview_dismissed'));
  const dismissPreviewWarning = useCallback(() => {
    setShowPreviewWarning(false);
    localStorage.setItem('vpsi_workspace_preview_dismissed', 'true');
  }, []);

  // Recording chip popover
  const [chipPopoverAnchor, setChipPopoverAnchor] = useState(null);
  const [chipPopoverRecId, setChipPopoverRecId] = useState(null);
  const [chipLabelDraft, setChipLabelDraft] = useState('');

  // Load API rules
  useEffect(() => {
    getAllRules().then(setApiRules).catch(() => {});
  }, []);

  // --- Derived tab state ---
  const activeTab = useMemo(() => getActiveTab(layoutConfig), [layoutConfig]);
  const tabWidgets = activeTab?.widgets || [];
  const tabNotes = activeTab?.notes || '';
  const tabMarkers = activeTab?.timelineMarkers || [];
  const tabRules = activeTab?.rules || [];
  const tabActiveRuleIds = activeTab?.activeRuleIds || [];

  // Combine all rule objects (API + global workspace-local + tab-local)
  const allRulesPool = useMemo(() => {
    const pool = [...apiRules];
    for (const r of (layoutConfig.globalRules || [])) {
      if (!pool.find((p) => p.id === r.id)) pool.push(r);
    }
    for (const r of tabRules) {
      if (!pool.find((p) => p.id === r.id)) pool.push(r);
    }
    return pool;
  }, [apiRules, layoutConfig.globalRules, tabRules]);

  // Merged active rules for event computation
  const activeRules = useMemo(
    () => mergeActiveRules(layoutConfig.globalActiveRuleIds || [], tabActiveRuleIds, allRulesPool),
    [layoutConfig.globalActiveRuleIds, tabActiveRuleIds, allRulesPool]
  );

  const eventsMap = useMemo(
    () => computeAllEvents(recordings, activeRules),
    [recordings, activeRules]
  );

  // --- Layout persistence ---
  const saveLayoutTimer = useRef(null);
  const persistLayoutConfig = useCallback((newLC) => {
    if (saveLayoutTimer.current) clearTimeout(saveLayoutTimer.current);
    saveLayoutTimer.current = setTimeout(() => {
      updateLayoutApi(workspaceId, newLC).catch(() => toast.error('Failed to save layout'));
    }, 800);
  }, [workspaceId]);

  const updateLayoutAndPersist = useCallback((newLC) => {
    setLayoutConfig(newLC);
    persistLayoutConfig(newLC);
  }, [persistLayoutConfig]);

  // --- Tab handlers ---
  const handleSelectTab = useCallback((tabId) => {
    const newLC = setActiveTab(layoutConfig, tabId);
    updateLayoutAndPersist(newLC);
  }, [layoutConfig, updateLayoutAndPersist]);

  const handleAddTab = useCallback(() => {
    const newLC = addTab(layoutConfig, 'New Tab');
    const newTab = newLC.tabs[newLC.tabs.length - 1];
    updateLayoutAndPersist({ ...newLC, activeTabId: newTab.id });
  }, [layoutConfig, updateLayoutAndPersist]);

  const handleRenameTab = useCallback((tabId, newName) => {
    updateLayoutAndPersist(renameTab(layoutConfig, tabId, newName));
  }, [layoutConfig, updateLayoutAndPersist]);

  const handleDeleteTab = useCallback((tabId) => {
    updateLayoutAndPersist(removeTab(layoutConfig, tabId));
  }, [layoutConfig, updateLayoutAndPersist]);

  const handleReorderTabs = useCallback((fromIndex, toIndex) => {
    updateLayoutAndPersist(reorderTabs(layoutConfig, fromIndex, toIndex));
  }, [layoutConfig, updateLayoutAndPersist]);

  const handleSetDefaultTab = useCallback((tabId) => {
    updateLayoutAndPersist(setDefaultTab(layoutConfig, tabId));
  }, [layoutConfig, updateLayoutAndPersist]);

  // --- Global notes & markers ---
  const saveNotesTimer = useRef(null);
  const handleUpdateGlobalNotes = useCallback((notes) => {
    setGlobalNotes(notes);
    if (saveNotesTimer.current) clearTimeout(saveNotesTimer.current);
    saveNotesTimer.current = setTimeout(() => {
      updateNotesApi(workspaceId, notes).catch(() => toast.error('Failed to save notes'));
    }, 800);
  }, [workspaceId]);

  const handleUpdateGlobalMarkers = useCallback((markers) => {
    setGlobalMarkers(markers);
    updateMarkersApi(workspaceId, markers).catch(() => toast.error('Failed to save markers'));
  }, [workspaceId]);

  // Tab notes & markers handlers are created per-tab via makeUpdateTabNotes/makeUpdateTabMarkers

  // --- Recording metadata ---
  const handleUpdateRecordingMetadata = useCallback((newMetadata) => {
    setRecordingMetadata(newMetadata);
    updateMetadataApi(workspaceId, newMetadata).catch(() => toast.error('Failed to save recording metadata'));
  }, [workspaceId]);

  // --- Workspace settings (persisted in layoutConfig) ---
  const handleUpdateSettings = useCallback((newSettings) => {
    setWorkspaceSettings(newSettings);
    const newLC = { ...layoutConfig, settings: newSettings };
    updateLayoutAndPersist(newLC);
  }, [layoutConfig, updateLayoutAndPersist]);

  // --- Rules ---
  const handleCreateRule = useCallback(async (rule) => {
    const scope = rule.scope || 'tab'; // 'global' or 'tab'
    const isWorkspaceOnly = rule.workspaceOnly || scope !== 'api';

    // Handle promoting a tab rule to workspace scope
    if (rule._promoteFromTab) {
      if (!activeTab) return;
      const cleanRule = { ...rule };
      delete cleanRule._promoteFromTab;
      cleanRule.scope = 'global';
      // Remove from tab, add to global
      let newLC = updateTab(layoutConfig, activeTab.id, {
        rules: tabRules.filter((r) => r.id !== rule.id),
        activeRuleIds: tabActiveRuleIds.filter((id) => id !== rule.id),
      });
      newLC = {
        ...newLC,
        globalRules: [...(newLC.globalRules || []), cleanRule],
        globalActiveRuleIds: [...(newLC.globalActiveRuleIds || []), cleanRule.id],
      };
      updateLayoutAndPersist(newLC);
      return;
    }

    // Handle demoting a workspace rule to tab scope
    if (rule._demoteFromGlobal) {
      if (!activeTab) return;
      const cleanRule = { ...rule };
      delete cleanRule._demoteFromGlobal;
      cleanRule.scope = 'tab';
      // Remove from global, add to tab
      let newLC = {
        ...layoutConfig,
        globalRules: (layoutConfig.globalRules || []).filter((r) => r.id !== rule.id),
        globalActiveRuleIds: (layoutConfig.globalActiveRuleIds || []).filter((id) => id !== rule.id),
      };
      newLC = updateTab(newLC, activeTab.id, {
        rules: [...tabRules, cleanRule],
        activeRuleIds: [...tabActiveRuleIds, cleanRule.id],
      });
      updateLayoutAndPersist(newLC);
      return;
    }

    if (scope === 'api' && !rule.workspaceOnly) {
      // Persist to API
      try {
        const saved = await createRule(rule);
        setApiRules((prev) => [...prev, saved]);
        // Auto-activate globally
        const newLC = {
          ...layoutConfig,
          globalActiveRuleIds: [...(layoutConfig.globalActiveRuleIds || []), saved.id],
        };
        updateLayoutAndPersist(newLC);
        toast.success('Rule created');
      } catch (err) {
        toast.error('Failed to create rule');
      }
    } else if (scope === 'global') {
      // Workspace-scoped global rule
      const newLC = {
        ...layoutConfig,
        globalRules: [...(layoutConfig.globalRules || []), rule],
        globalActiveRuleIds: [...(layoutConfig.globalActiveRuleIds || []), rule.id],
      };
      updateLayoutAndPersist(newLC);
    } else {
      // Tab-scoped rule
      if (!activeTab) return;
      const newLC = updateTab(layoutConfig, activeTab.id, {
        rules: [...tabRules, rule],
        activeRuleIds: [...tabActiveRuleIds, rule.id],
      });
      updateLayoutAndPersist(newLC);
    }
  }, [layoutConfig, activeTab, tabRules, tabActiveRuleIds, updateLayoutAndPersist]);

  const handleToggleGlobalRule = useCallback((rule) => {
    const ids = layoutConfig.globalActiveRuleIds || [];
    const newIds = ids.includes(rule.id) ? ids.filter((id) => id !== rule.id) : [...ids, rule.id];
    updateLayoutAndPersist({ ...layoutConfig, globalActiveRuleIds: newIds });
  }, [layoutConfig, updateLayoutAndPersist]);

  // --- Widget handlers (tab-scoped, accept tabId for multi-tab rendering) ---
  const handleAddWidget = useCallback(async (widgetConfig) => {
    if (!activeTab) return;
    const tab = activeTab;
    const newWidgets = [...tab.widgets, widgetConfig];
    const newLC = updateTab(layoutConfig, tab.id, { widgets: newWidgets });
    updateLayoutAndPersist(newLC);
  }, [activeTab, layoutConfig, updateLayoutAndPersist]);

  const makeRemoveWidget = useCallback((tabId) => (widgetId) => {
    const tab = (layoutConfig.tabs || []).find((t) => t.id === tabId);
    if (!tab) return;
    const newWidgets = tab.widgets.filter((w) => w.id !== widgetId);
    const newLC = updateTab(layoutConfig, tabId, { widgets: newWidgets });
    updateLayoutAndPersist(newLC);
  }, [layoutConfig, updateLayoutAndPersist]);

  const makeUpdateWidget = useCallback((tabId) => (widgetId, updates) => {
    const tab = (layoutConfig.tabs || []).find((t) => t.id === tabId);
    if (!tab) return;
    const newWidgets = tab.widgets.map((w) => (w.id === widgetId ? { ...w, ...updates } : w));
    const newLC = updateTab(layoutConfig, tabId, { widgets: newWidgets });
    updateLayoutAndPersist(newLC);
  }, [layoutConfig, updateLayoutAndPersist]);

  const makeLayoutChange = useCallback((tabId) => (updatedWidgets) => {
    const newLC = updateTab(layoutConfig, tabId, { widgets: updatedWidgets });
    updateLayoutAndPersist(newLC);
  }, [layoutConfig, updateLayoutAndPersist]);

  const makeUpdateTabNotes = useCallback((tabId) => (notes) => {
    const newLC = updateTab(layoutConfig, tabId, { notes });
    updateLayoutAndPersist(newLC);
  }, [layoutConfig, updateLayoutAndPersist]);

  const makeUpdateTabMarkers = useCallback((tabId) => (markers) => {
    const newLC = updateTab(layoutConfig, tabId, { timelineMarkers: markers });
    updateLayoutAndPersist(newLC);
  }, [layoutConfig, updateLayoutAndPersist]);

  const makeToggleTabRule = useCallback((tabId) => (rule) => {
    const tab = (layoutConfig.tabs || []).find((t) => t.id === tabId);
    if (!tab) return;
    const ids = tab.activeRuleIds || [];
    const newIds = ids.includes(rule.id) ? ids.filter((id) => id !== rule.id) : [...ids, rule.id];
    const newLC = updateTab(layoutConfig, tabId, { activeRuleIds: newIds });
    updateLayoutAndPersist(newLC);
  }, [layoutConfig, updateLayoutAndPersist]);

  // --- Fetch workspace ---
  const fetchWorkspace = useCallback(async () => {
    try {
      let ws = await getWorkspace(workspaceId);

      // Migrate old workspaces to tabbed layout
      if (needsMigration(ws)) {
        ws = migrateWorkspace(ws);
        // Persist the migration
        await updateLayoutApi(workspaceId, ws.layoutConfig).catch(() => {});
      }

      setWorkspace(ws);

      // Initialize layout config — use defaultTabId as activeTabId on fresh load
      const lc = ws.layoutConfig || { tabs: [], widgets: [] };
      if (lc.defaultTabId && !lc.activeTabId) {
        lc.activeTabId = lc.defaultTabId;
      } else if (!lc.activeTabId && lc.tabs?.length > 0) {
        lc.activeTabId = lc.tabs[0].id;
      }
      setLayoutConfig(lc);

      setGlobalNotes(ws.notes || '');
      setGlobalMarkers(ws.timelineMarkers || []);
      setRecordingMetadata(ws.recordingMetadata || {});
      if (lc.settings) setWorkspaceSettings(lc.settings);

      const recordingData = await Promise.all(
        (ws.recordingIds || []).map((id) => getDataCapture(id).catch(() => null))
      );
      setRecordings(recordingData.filter(Boolean).sort((a, b) => (a.startTime || 0) - (b.startTime || 0)));
      setLoading(false);
    } catch (err) {
      toast.error('Failed to load workspace');
      navigate('/dashboard/workspaces');
    }
  }, [workspaceId, navigate]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  useEffect(() => {
    if (workspace?.name && setPageTitle) setPageTitle(workspace.name);
    return () => { if (setPageTitle) setPageTitle(''); };
  }, [workspace?.name, setPageTitle]);

  useEffect(() => {
    if (workspace && recordings.length === 0 && !loading) {
      getDataCapturesList().then(setAllRecordings).catch(() => {});
      setAddRecordingDialogOpen(true);
    }
  }, [workspace, recordings.length, loading]);

  // --- Add recordings ---
  const handleAddRecordings = async () => {
    if (selectedRecordingIds.length === 0) return;
    const isFirstRecording = recordings.length === 0;
    try {
      let latestWs = workspace;
      const newRecs = [];
      for (const recId of selectedRecordingIds) {
        latestWs = await addRecordingApi(workspaceId, recId);
        const rec = await getDataCapture(recId).catch(() => null);
        if (rec) newRecs.push(rec);
      }
      setWorkspace(latestWs);
      setRecordings((prev) => [...prev, ...newRecs].sort((a, b) => (a.startTime || 0) - (b.startTime || 0)));
      setAddRecordingDialogOpen(false);
      setSelectedRecordingIds([]);
      setRecordingSearch('');

      // Auto-assign colors
      const existingCount = Object.keys(recordingMetadata).length;
      const newMeta = { ...recordingMetadata };
      newRecs.forEach((rec, idx) => {
        if (!newMeta[rec.id]) {
          newMeta[rec.id] = { color: RECORDING_COLORS[(existingCount + idx) % RECORDING_COLORS.length].value, label: '' };
        }
      });
      setRecordingMetadata(newMeta);
      updateMetadataApi(workspaceId, newMeta).catch(() => {});

      toast.success(`${newRecs.length} recording${newRecs.length !== 1 ? 's' : ''} added`);

      // Auto-create widgets on active tab (only on first recording add)
      if (isFirstRecording && newRecs.length > 0 && activeTab) {
        const currentWidgets = activeTab.widgets || [];
        const newWidgets = [];
        const hasVideo = newRecs.some((r) => !r.sensorsOnly);

        // Row 1: Unified Video (4) + Rules (4) + Notes (4)
        if (hasVideo) {
          newWidgets.push({
            id: uuidv4(), type: 'unified_video', recordingId: null, sensorKey: null,
            position: { x: 0, y: 0, w: 4, h: 4 },
          });
        }
        newWidgets.push({
          id: uuidv4(), type: 'rules', recordingId: null, sensorKey: null,
          position: { x: hasVideo ? 4 : 0, y: 0, w: 4, h: 4 },
        });
        newWidgets.push({
          id: uuidv4(), type: 'notes', recordingId: null, sensorKey: null,
          position: { x: hasVideo ? 8 : 4, y: 0, w: 4, h: 4 },
        });

        // Row 2: Unified Sensor Chart (full width)
        newWidgets.push({
          id: uuidv4(), type: 'timeline_sensor_chart', recordingId: null, sensorKey: null,
          position: { x: 0, y: 4, w: 12, h: 4 },
        });

        const updatedWidgets = [...currentWidgets, ...newWidgets];
        const newLC = updateTab(layoutConfig, activeTab.id, { widgets: updatedWidgets });
        updateLayoutAndPersist(newLC);
      }
    } catch (err) {
      toast.error('Failed to add recording(s)');
    }
  };

  // --- Remove recording ---
  const handleRemoveRecording = async (recordingId) => {
    try {
      const ws = await removeRecordingApi(workspaceId, recordingId);
      setWorkspace(ws);
      setRecordings((prev) => prev.filter((r) => r.id !== recordingId));
      // Remove widgets for this recording from ALL tabs
      const newLC = removeRecordingWidgets(layoutConfig, recordingId);
      updateLayoutAndPersist(newLC);
      setRecordingMetadata((prev) => {
        const next = { ...prev };
        delete next[recordingId];
        return next;
      });
      toast.success('Recording removed');
    } catch (err) {
      toast.error('Failed to remove recording');
    }
  };

  const openAddRecordingDialog = async () => {
    try {
      const all = await getDataCapturesList();
      setAllRecordings(all);
    } catch (err) {
      // Fall through
    }
    setAddRecordingDialogOpen(true);
  };

  // --- Cross-tab marker data for timeline ---
  const allTabMarkers = useMemo(() => {
    return (layoutConfig.tabs || []).map((tab) => ({
      tabId: tab.id,
      tabName: tab.name,
      markers: tab.timelineMarkers || [],
    }));
  }, [layoutConfig.tabs]);

  // --- Cross-tab rules data for rules widget ---
  const otherTabsRules = useMemo(() => {
    return (layoutConfig.tabs || [])
      .filter((tab) => tab.id !== layoutConfig.activeTabId)
      .map((tab) => ({
        tabId: tab.id,
        tabName: tab.name,
        rules: tab.rules || [],
      }));
  }, [layoutConfig.tabs, layoutConfig.activeTabId]);

  const handleSwitchToTabFromMarker = useCallback((tabId) => {
    handleSelectTab(tabId);
  }, [handleSelectTab]);

  if (loading) return <LoadingSpinner message="Loading workspace..." />;
  if (!workspace) return null;

  const bounds = computeTimelineBounds(recordings);
  const hasTimeline = bounds.end > bounds.start;
  const availableRecordings = allRecordings
    .filter((r) => !workspace.recordingIds?.includes(r.id))
    .sort((a, b) => (b.startTime || b.created || 0) - (a.startTime || a.created || 0));

  const filteredAvailableRecordings = recordingSearch.trim()
    ? availableRecordings.filter((r) => r.name?.toLowerCase().includes(recordingSearch.toLowerCase()))
    : availableRecordings;

  return (
    <>
      <Helmet>
        <title>VPSI | {workspace.name}</title>
      </Helmet>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', mx: { xs: 0, lg: -2 }, mt: { xs: 0, lg: -3 }, mb: -10 }}>
        {/* Feature preview warning */}
        {showPreviewWarning && (
          <Alert severity="warning" onClose={dismissPreviewWarning} sx={{ flexShrink: 0, borderRadius: 0 }}>
            <AlertTitle>Early Feature Preview</AlertTitle>
            This is a very early feature preview. There might be bugs with the workspaces feature and we are currently trying to address them. Please let Kyle know at{' '}
            <a href="mailto:kyle.fischer@videoplussensors.com">kyle.fischer@videoplussensors.com</a> if there are any that you come across.
          </Alert>
        )}

        {/* Toolbar */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, bgcolor: 'background.default' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Recordings:</Typography>
            {recordings.map((rec) => {
              const meta = recordingMetadata[rec.id];
              const color = meta?.color || '#1976d2';
              const displayLabel = meta?.label || rec.name;
              return (
                <Chip
                  key={rec.id}
                  label={displayLabel}
                  size="small"
                  sx={{
                    bgcolor: color,
                    color: '#fff',
                    fontWeight: 500,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.85 },
                  }}
                  onClick={(e) => {
                    setChipPopoverAnchor(e.currentTarget);
                    setChipPopoverRecId(rec.id);
                    setChipLabelDraft(meta?.label || '');
                  }}
                />
              );
            })}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:film-fill" />} onClick={openAddRecordingDialog}>
              Add Recording
            </Button>
            <Button size="small" variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setPickerOpen(true)} disabled={recordings.length === 0}>
              Add Widget
            </Button>
          </Stack>
        </Stack>

        {/* Tab Bar */}
        <TabBar
          tabs={layoutConfig.tabs || []}
          activeTabId={layoutConfig.activeTabId}
          defaultTabId={layoutConfig.defaultTabId}
          onSelectTab={handleSelectTab}
          onAddTab={handleAddTab}
          onRenameTab={handleRenameTab}
          onDeleteTab={handleDeleteTab}
          onReorderTabs={handleReorderTabs}
          onSetDefault={handleSetDefaultTab}
          onOpenSettings={() => setSettingsDrawerOpen(true)}
        />

        {/* Content */}
        {hasTimeline ? (
          <TimelineProvider
            bounds={bounds}
            recordings={recordings}
            events={Object.values(eventsMap).flat()}
            skipEmptyTime={workspaceSettings.skipEmptyTime}
            highlightReelMode={workspaceSettings.highlightReelMode}
            highlightReelLag={workspaceSettings.highlightReelLag ?? 5}
          >
            <Box sx={{ flex: 1, overflow: 'auto', pt: 5, pb: 12 }}>
              {(layoutConfig.tabs || []).map((tab) => {
                const isActive = tab.id === layoutConfig.activeTabId;
                return (
                  <Box key={tab.id} sx={{ display: isActive ? 'block' : 'none' }}>
                    <WorkspaceGrid
                      workspaceId={workspaceId}
                      widgets={tab.widgets || []}
                      recordings={recordings}
                      recordingMetadata={recordingMetadata}
                      onRemoveWidget={makeRemoveWidget(tab.id)}
                      onUpdateWidget={makeUpdateWidget(tab.id)}
                      eventsMap={eventsMap}
                      rules={allRulesPool}
                      activeRules={activeRules}
                      globalRules={layoutConfig.globalRules || []}
                      globalActiveRuleIds={layoutConfig.globalActiveRuleIds || []}
                      tabRules={tab.rules || []}
                      tabActiveRuleIds={tab.activeRuleIds || []}
                      onToggleGlobalRule={handleToggleGlobalRule}
                      onToggleTabRule={makeToggleTabRule(tab.id)}
                      onCreateRule={handleCreateRule}
                      workspaceNotes={globalNotes}
                      workspaceMarkers={globalMarkers}
                      tabNotes={tab.notes || ''}
                      tabMarkers={tab.timelineMarkers || []}
                      onUpdateNotes={handleUpdateGlobalNotes}
                      onUpdateMarkers={handleUpdateGlobalMarkers}
                      onUpdateTabNotes={makeUpdateTabNotes(tab.id)}
                      onUpdateTabMarkers={makeUpdateTabMarkers(tab.id)}
                      onLayoutChange={makeLayoutChange(tab.id)}
                      otherTabsRules={otherTabsRules}
                    />
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0, position: 'sticky', bottom: 0, zIndex: 10, bgcolor: 'background.default' }}>
              <UnifiedTimeline
                recordings={recordings}
                eventsMap={eventsMap}
                globalMarkers={globalMarkers}
                tabMarkers={tabMarkers}
                activeTabId={layoutConfig.activeTabId}
                allTabMarkers={allTabMarkers}
                onSwitchToTab={handleSwitchToTabFromMarker}
                recordingMetadata={recordingMetadata}
              />
            </Box>
          </TimelineProvider>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Stack alignItems="center" spacing={2}>
              <Iconify icon="eva:film-outline" width={48} sx={{ color: 'text.disabled' }} />
              <Typography variant="h6" color="text.secondary">No recordings yet</Typography>
              <Typography variant="body2" color="text.disabled">Add recordings to start building your workspace</Typography>
              <Button variant="contained" onClick={openAddRecordingDialog}>Add Recording</Button>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Add Recording Dialog */}
      <Dialog open={addRecordingDialogOpen} onClose={() => { setAddRecordingDialogOpen(false); setRecordingSearch(''); setSelectedRecordingIds([]); }} maxWidth="sm" fullWidth>
        <DialogTitle>{recordings.length === 0 ? 'Select Recording(s) to Get Started' : 'Add Recordings'}</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search recordings..."
            value={recordingSearch}
            onChange={(e) => setRecordingSearch(e.target.value)}
            fullWidth
            sx={{ mt: 1, mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={20} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          {selectedRecordingIds.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
              {selectedRecordingIds.map((id) => {
                const rec = allRecordings.find((r) => r.id === id);
                return (
                  <Chip
                    key={id}
                    label={rec?.name || id}
                    size="small"
                    onDelete={() => setSelectedRecordingIds((prev) => prev.filter((r) => r !== id))}
                    color="primary"
                    variant="outlined"
                  />
                );
              })}
            </Stack>
          )}
          <List sx={{ maxHeight: 320, overflow: 'auto' }} dense>
            {filteredAvailableRecordings.map((rec) => {
              const isSelected = selectedRecordingIds.includes(rec.id);
              return (
                <ListItemButton
                  key={rec.id}
                  selected={isSelected}
                  onClick={() => setSelectedRecordingIds((prev) =>
                    isSelected ? prev.filter((r) => r !== rec.id) : [...prev, rec.id]
                  )}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" checked={isSelected} size="small" tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={rec.name}
                    secondary={rec.startTime ? new Date(rec.startTime).toLocaleDateString() + ' ' + new Date(rec.startTime).toLocaleTimeString() : '—'}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              );
            })}
            {filteredAvailableRecordings.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                {availableRecordings.length === 0 ? 'All recordings are already in this workspace.' : 'No recordings match your search.'}
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddRecordingDialogOpen(false); setRecordingSearch(''); setSelectedRecordingIds([]); }}>Cancel</Button>
          <Button onClick={handleAddRecordings} variant="contained" disabled={selectedRecordingIds.length === 0}>
            Add {selectedRecordingIds.length > 0 ? `(${selectedRecordingIds.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Recording Confirmation */}
      <Dialog open={removeRecordingId != null} onClose={() => setRemoveRecordingId(null)}>
        <DialogTitle>Remove Recording</DialogTitle>
        <DialogContent>
          Are you sure you want to remove "{recordings.find((r) => r.id === removeRecordingId)?.name}"?
          All widgets linked to this recording will also be removed from all tabs.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveRecordingId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              handleRemoveRecording(removeRecordingId);
              setRemoveRecordingId(null);
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recording Chip Popover */}
      <Popover
        open={chipPopoverAnchor != null}
        anchorEl={chipPopoverAnchor}
        onClose={() => {
          if (chipPopoverRecId && chipLabelDraft !== (recordingMetadata[chipPopoverRecId]?.label || '')) {
            handleUpdateRecordingMetadata({
              ...recordingMetadata,
              [chipPopoverRecId]: { ...recordingMetadata[chipPopoverRecId], label: chipLabelDraft },
            });
          }
          setChipPopoverAnchor(null);
          setChipPopoverRecId(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 220 }}>
          <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
            {recordings.find((r) => r.id === chipPopoverRecId)?.name}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
            {RECORDING_COLORS.map((c) => (
              <Box
                key={c.value}
                sx={{
                  width: 24, height: 24, borderRadius: '50%', bgcolor: c.value, cursor: 'pointer',
                  border: '2px solid',
                  borderColor: recordingMetadata[chipPopoverRecId]?.color === c.value ? 'text.primary' : 'transparent',
                  '&:hover': { transform: 'scale(1.2)' },
                  transition: 'transform 0.1s',
                }}
                title={c.label}
                onClick={() => {
                  handleUpdateRecordingMetadata({
                    ...recordingMetadata,
                    [chipPopoverRecId]: { ...recordingMetadata[chipPopoverRecId], color: c.value },
                  });
                }}
              />
            ))}
          </Stack>
          <TextField
            size="small"
            placeholder="Label (optional)"
            value={chipLabelDraft}
            onChange={(e) => setChipLabelDraft(e.target.value)}
            fullWidth
            sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleUpdateRecordingMetadata({
                  ...recordingMetadata,
                  [chipPopoverRecId]: { ...recordingMetadata[chipPopoverRecId], label: chipLabelDraft },
                });
              }
            }}
          />
          <Button
            size="small"
            color="error"
            fullWidth
            startIcon={<Iconify icon="eva:trash-2-outline" />}
            onClick={() => {
              setChipPopoverAnchor(null);
              setRemoveRecordingId(chipPopoverRecId);
              setChipPopoverRecId(null);
            }}
          >
            Remove Recording
          </Button>
        </Box>
      </Popover>

      {/* Widget Picker */}
      <WidgetPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdd={handleAddWidget}
        recordings={recordings}
      />

      {/* Workspace Settings Drawer */}
      <WorkspaceSettingsDrawer
        open={settingsDrawerOpen}
        onClose={() => setSettingsDrawerOpen(false)}
        settings={workspaceSettings}
        onUpdateSettings={handleUpdateSettings}
      />
    </>
  );
}
